import {
  DelegaModulo,
  EnumInfoDelegaTipo,
  EnumModuloDTONecessitaFirma,
  RichiestaModuloDTO,
} from '@framework/api-client';
import { IDelegation, ISubmissionManager } from './IDelegation';

export interface IModuleDelegation {
  submissionModule: RichiestaModuloDTO;
  compilation: IDelegation | null;
  sign: IDelegation | null;
  view: IDelegation[];
}

export interface IDelegationData {
  manager: ISubmissionManager | null | undefined;
  attorney: ISubmissionManager | null | undefined;
  applicant: ISubmissionManager[] | null | undefined;
  moduleDelegation: IModuleDelegation[];
}

export class DelegationLogic {
  static getNameWithDefault(data: {
    nome?: string | null;
    codiceFiscale?: string | null;
  }) {
    if (data.nome) return data.nome;
    return data.codiceFiscale ?? '';
  }

  static getModuleDelegations(input: {
    modules: RichiestaModuloDTO[];
    data: DelegaModulo[];
  }): IModuleDelegation[] {
    const { modules, data } = input;

    return modules.map(module => {
      const moduleData = data.find(d => d.idRichiestaModulo === module.id);
      const compilation =
        moduleData?.deleghe?.find(
          d => d.tipo === EnumInfoDelegaTipo.COMPILAZIONE,
        ) ?? null;
      const sign =
        moduleData?.deleghe?.find(d => d.tipo === EnumInfoDelegaTipo.FIRMA) ??
        null;
      const view =
        moduleData?.deleghe?.filter(
          d => d.tipo === EnumInfoDelegaTipo.VISUALIZZAZIONE,
        ) ?? [];

      return {
        submissionModule: module,
        compilation: compilation
          ? {
              fiscalCode: compilation.codiceFiscale,
              type: compilation.tipo!,
              userId: compilation.idUtente,
              userName: this.getNameWithDefault(compilation),
            }
          : null,
        sign: sign
          ? {
              fiscalCode: sign.codiceFiscale,
              type: sign.tipo!,
              userId: sign.idUtente,
              userName: this.getNameWithDefault(sign),
            }
          : null,
        view: view.map(v => ({
          fiscalCode: v.codiceFiscale,
          type: v.tipo!,
          userId: v.idUtente,
          userName: this.getNameWithDefault(v),
        })),
      };
    });
  }

  /**
   * Ritorna vero se tutti i moduli hanno una delega associata
   * oppure se nessun modulo ha una delega associata.
   *
   * @param delegations Lista delle deleghe *dello stesso tipo* (compilazione, firma, visualizzazione)
   */
  static checkAllModuleHasDelegation(
    delegations: IDelegation[] | IDelegation[][],
    modulesDelegations: IModuleDelegation[],
  ) {
    return (
      delegations.length === 0 ||
      modulesDelegations.length === delegations.length
    );
  }

  /**
   * Ritorna la lista dei delegati unici.
   */
  static getUniqueDelegations(delegations: IDelegation[]) {
    return delegations.filter(
      (value, index, self) =>
        self.findIndex(other => other?.fiscalCode === value?.fiscalCode) ===
        index,
    );
  }

  /* Ritorna `true` se tutti i moduli hanno gli stessi delegati di visualizzazione */
  static checkUniqueViewsDelegations(
    view: IDelegation[][],
    uniqueView: IDelegation[],
  ) {
    return view.every(viewDelegations =>
      uniqueView.every(delegation =>
        viewDelegations.some(vd => vd.fiscalCode === delegation.fiscalCode),
      ),
    );
  }

  /**
   * Ritorna il flag che indica se il form di modifica delle deleghe in esame è abilitato.
   */
  static getInitialEnableFlag(delegations: IDelegation[]) {
    return delegations.length > 0;
  }

  /**
   * Controlla se almeno un modulo richiede la firma.
   */
  static hasAtleastOneSignableModule(delegations: IModuleDelegation[]) {
    return delegations.some(
      d =>
        d.submissionModule?.modulo?.modulo?.necessitaFirma !==
        EnumModuloDTONecessitaFirma.NO,
    );
  }

  /**
   * Ritorna il flag che indica se la modifica delle deleghe è stata confermata.
   */
  static getInitialConfirmUpdateFlag(
    delegations: IDelegation[] | IDelegation[][],
    areAllDelegates: boolean,
  ) {
    // Se ci sono 1 o meno delegati e i delegati sono uguali per tutti i moduli
    return delegations.length < 2 && areAllDelegates;
  }
}
